//
// TMS layer (tiled vector layer), used for read-only geometries

import * as L from "leaflet"
import {VectorLayer} from "@/engine/layers/vectorLayer.js";
import VectorTileLayer, {
    applyBasicStyle, applyPathStyle,
    defaultFeatureLayer,
    featureLayerBase
} from "@/engine/leaflet/VectorTileLayer.js";
import {elog} from "@/engine/helpers/logging.js";
import {shortLivedTokenManager} from "@/context/global.js";

class VectorLayerTMS extends VectorLayer {

    constructor(struct) {
        let tmsStyleKey = struct.tmsStyleKey ? struct.tmsStyleKey : "tms"

        if (! struct.defaultStyle)
            struct.defaultStyle = {
            "tms" : {
                "color": 'red',
                "opacity": 1,
                "weight": 1,
                "fill": false
            }
        }

        // to migrate an old style saved data to a new format
        struct.migrationStyleTo = struct.tmsStyleKey ? struct.tmsStyleKey : tmsStyleKey
        struct.defaultStyleKey = struct.defaultStyleKey ? struct.defaultStyleKey : tmsStyleKey
        super(struct)

        this.TMSLayer = null
        this.tmsRequestHeaders = {}
        this.tokenChangeUnsubscribe = null
        this.authorizationChangeHandler = this._setAuthorizationHeader.bind(this)
        this.typeCommon = true // is used by mustache templates
        this.tmsStyleKey = tmsStyleKey

        this.styleKeys = struct.styleKeys ? struct.styleKeys :
            {'tms' : this.tmsLayerSetStyle.bind(this)}
        this.defaultRadius = 1 // default radius for point/circles
        this.minDisplayZoom = null;
        this.minSizeInPxToShow = 10;
        let self = this;
        this.filter = function (feature, layer, zoom) {
            return true
        }

        this.simplificationTolerance = 1;
        /*
        this.debug = {
            featuresTotal: 0,
            featuresFiltered: 0,
            featuresAdded: 0,
            vertexesTotal: 0,
            vertexesFiltered: 0,
            vertexesAdded: 0,
        }
         */

        // feature types array resolving pbf feature.type field value (borrowed from VectorTileLayer)
        //this.featureTypes = ['Unknown', 'Point', 'LineString', 'Polygon'];
        this.FEATURE_TYPE_UNKNOWN = 0
        this.FEATURE_TYPE_POINT = 1
        this.FEATURE_TYPE_LINESTRING = 2
        this.FEATURE_TYPE_POLYGON = 3

        this.TMSLayerOptions = {
            fetchOptions: {
                headers: this.tmsRequestHeaders,
            },
            style: this.getStyle(this.tmsStyleKey),
            interactive: false,
            minDetailZoom: 10,
            bounds: this.bounds,
            zIndex: 50,
            // base feature filter, hide features at low zoom levels
            // and call custom filter if defined
            filter: function (feature, layer, zoom) {
                // calculate minimum zoom to display layer
                if ( self.minDisplayZoom == null && self.map && self.bounds) {
                    let s = L.bounds(
                        self.map.project(self.bounds.getSouthWest()),
                        self.map.project(self.bounds.getNorthEast())
                    ).getSize()

                    let size = Math.max(s.x, s.y)
                    let scale = size / self.minSizeInPxToShow;
                    if (scale) {
                        // at this zoom it is shown with WORKSPACE_MIN_SIZE_IN_PIXELS_TO_SHOW size
                        self.minDisplayZoom = Math.ceil(self.map.getScaleZoom(1 / scale))
                    } else {
                        self.minDisplayZoom = 0
                    }
                }
                if (zoom < self.minDisplayZoom) {
                    return false
                }
                // calls custom defined filter
                return self.filter(feature, layer, zoom)
            },

            // builds geometry layer from feature
            featureToLayer: function (feature, layerName, pxPerExtent, options) {
                //self.debug.featuresTotal++;
                const line = self.featureLayer(
                    feature,
                    layerName,
                    pxPerExtent,
                    options
                );

                if (!line) {
                    // feature is filtered
//                    self.debug.featuresFiltered++
                    return null
                }

                //self.debug.featuresAdded++;

                // Construct a base feature layer.
                const layer = featureLayerBase(feature, layerName, pxPerExtent, options);

                const group = L.SVG.create("g");
                group.appendChild(line.graphics);
                layer.graphics = group;

                // Setting of style is delegated to the sub layers.
                layer.setStyle = function setStyle(style) {
                    // setting default radius of points
                    if (feature.type === self.FEATURE_TYPE_POINT) {
                        if (!style) {
                            style = { radius: self.defaultRadius}
                        } else {
                            if ( ! ( 'radius' in style)) {
                                style.radius = self.defaultRadius
                            }
                        }
                        line.setStyle({ radius: self.defaultRadius})
                    }
                    line.setStyle(style);
                };

                // Initial setup of this feature layer.
                layer.applyOptions(options);

                return layer;
            }
        }
        if (new.target === VectorLayerTMS) {
            this._fireNew();
        }
    }

    // based on VectorTileLayer.featurePathLayer
    // with our logic to filtering geometries
    // and do simplification of polygons
    featureLayer(feature, layerName, pxPerExtent, options) {
        if (feature.type !== this.FEATURE_TYPE_POLYGON &&
            feature.type !== this.FEATURE_TYPE_LINESTRING)
            return defaultFeatureLayer(feature, layerName, pxPerExtent, options)

        options = L.extend(
            {},
            (
                feature.type === this.FEATURE_TYPE_POLYGON
                    ? L.Polygon.prototype.options
                    : L.Path.prototype.options
            ),
            options
        );

        // get feature geometry size
        const bbox = feature.bbox()

        // exclude small polygons at low zoomlevels
        if (Math.abs(bbox[0] - bbox[2]) * pxPerExtent.x < 2 && Math.abs(bbox[1] - bbox[3]) * pxPerExtent.y < 2) {
            return null
        }

        const geometry = feature.loadGeometry();
        const tolerance = this.simplificationTolerance / pxPerExtent.x;
        const self = featureLayerBase(feature, layerName, pxPerExtent, options);

        self.graphics = L.SVG.create("path");
        self.graphics.setAttribute("d", L.SVG.pointsToPath(
            geometry.map((ring) => L.LineUtil.simplify(ring, tolerance).map(self.scalePoint)),
            feature.type === this.FEATURE_TYPE_POLYGON
        ));

        self.setStyle = function setStyle(style) {
            style = L.extend({}, options, style);
            applyBasicStyle(self.graphics, style);
            applyPathStyle(self.graphics, style);
        };

        self.applyOptions(options);

        return self
    }

    addOnMap(map) {
        this._bindTokenAuthorization()
        super.addOnMap(map, function () {
            if (this.TMSLayer != null)
                return this.TMSLayer

            this.TMSLayer = VectorTileLayer(
                this._joinServerUrl(`gwc/service/tms/1.0.0/${this.workspace.id}:${this.id}@EPSG%3A900913@pbf/{z}/{x}/{-y}.pbf`),
                this.TMSLayerOptions
            )

            this.TMSLayer.on("tileerror", function (ev) {
                //suppress massive error logging
                if (!ev.target.err_count)  {
                    ev.target.err_count = 1;
                } else {
                    ev.target.err_count++;
                }

                if (ev.target.err_count > 10) {
                    return;
                }
                let e = ev.error.message.split(":",4)
                let url = "";
                let code = 0;
                if (e.length === 4) {
                    url = e[0] + ":" + e[1];
                    code = e[2] * 1;
                }
                elog("WARN", `Can't load tile ${url}, error code: ${code}`);
            }.bind(this))

            return this.TMSLayer

        }.bind(this))
    }

    /**
     * Removes the TMS layer and releases its token-change subscription.
     * @returns {void}
     */
    removeFromMap() {
        this._unbindTokenAuthorization()
        super.removeFromMap()
    }

    /**
     * Initializes the current Authorization value and subscribes to token changes.
     * Repeated calls preserve the existing subscription.
     * @returns {void}
     * @private
     */
    _bindTokenAuthorization() {
        this._setAuthorizationHeader(shortLivedTokenManager.getAuthorizationHeader())
        if (this.tokenChangeUnsubscribe == null) {
            this.tokenChangeUnsubscribe = shortLivedTokenManager.onChange(this.authorizationChangeHandler)
        }
    }

    /**
     * Releases the token-change subscription and removes the transport credential.
     * @returns {void}
     * @private
     */
    _unbindTokenAuthorization() {
        if (this.tokenChangeUnsubscribe != null) {
            this.tokenChangeUnsubscribe()
            this.tokenChangeUnsubscribe = null
        }
        this._setAuthorizationHeader(null)
    }

    /**
     * Updates the reusable TMS request headers with a prepared Authorization value.
     * @param {string|null} authorizationHeader Prepared Bearer value or null.
     * @returns {void}
     * @private
     */
    _setAuthorizationHeader(authorizationHeader) {
        if (authorizationHeader == null) {
            delete this.tmsRequestHeaders.Authorization
            return
        }
        this.tmsRequestHeaders.Authorization = authorizationHeader
    }

    tmsLayerSetStyle() {
        if (this.layer && this.layer.setStyle) {
            this.layer.setStyle(this.style[this.tmsStyleKey])
        }
    }
}

export {VectorLayerTMS};
